import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type TeamRole = 'leader' | 'member';

export interface Team {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
}

export interface TeamMember {
  id: string;
  team_id: string;
  user_id: string;
  role: TeamRole;
  created_at: string;
}

export interface TeamMemberWithProfile extends TeamMember {
  profile: {
    user_id: string;
    display_name: string | null;
  } | null;
}

export interface TeamWithMembers extends Team {
  members: TeamMemberWithProfile[];
  memberCount: number;
  leaderCount: number;
}

// Fetch all teams
export function useTeams(orgId?: string | null) {
  return useQuery({
    queryKey: ["crm-teams", orgId],
    queryFn: async () => {
      let query = supabase
        .from("crm_teams")
        .select("*")
        .order("name");
      
      if (orgId) {
        query = query.eq("organization_id", orgId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as Team[];
    },
    enabled: !!orgId,
  });
}

// Fetch a single team with members
export function useTeam(id: string | undefined) {
  return useQuery({
    queryKey: ["crm-team", id],
    queryFn: async () => {
      if (!id) return null;
      
      const { data: team, error: teamError } = await supabase
        .from("crm_teams")
        .select("*")
        .eq("id", id)
        .single();
      
      if (teamError) throw teamError;
      
      const { data: members, error: membersError } = await supabase
        .from("crm_team_members")
        .select("*")
        .eq("team_id", id);
      
      if (membersError) throw membersError;
      
      // Fetch profiles for members
      const userIds = members.map(m => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", userIds);
      
      if (profilesError) throw profilesError;
      
      const membersWithProfiles: TeamMemberWithProfile[] = members.map(m => ({
        ...m,
        role: m.role as TeamRole,
        profile: profiles.find(p => p.user_id === m.user_id) || null,
      }));
      
      return {
        ...team,
        members: membersWithProfiles,
        memberCount: members.filter(m => m.role === 'member').length,
        leaderCount: members.filter(m => m.role === 'leader').length,
      } as TeamWithMembers;
    },
    enabled: !!id,
  });
}

// Fetch teams with member counts
export function useTeamsWithCounts(orgId?: string | null) {
  return useQuery({
    queryKey: ["crm-teams-with-counts", orgId],
    queryFn: async () => {
      let teamsQuery = supabase
        .from("crm_teams")
        .select("*")
        .order("name");
      
      let membersQuery = supabase
        .from("crm_team_members")
        .select("*");
      
      if (orgId) {
        teamsQuery = teamsQuery.eq("organization_id", orgId);
        membersQuery = membersQuery.eq("organization_id", orgId);
      }
      
      const { data: teams, error: teamsError } = await teamsQuery;
      if (teamsError) throw teamsError;
      
      const { data: members, error: membersError } = await membersQuery;
      if (membersError) throw membersError;
      
      // Fetch profiles for all members
      const userIds = [...new Set(members.map(m => m.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", userIds);
      
      return teams.map(team => {
        const teamMembers = members.filter(m => m.team_id === team.id);
        const membersWithProfiles: TeamMemberWithProfile[] = teamMembers.map(m => ({
          ...m,
          role: m.role as TeamRole,
          profile: profiles?.find(p => p.user_id === m.user_id) || null,
        }));
        
        return {
          ...team,
          members: membersWithProfiles,
          memberCount: teamMembers.filter(m => m.role === 'member').length,
          leaderCount: teamMembers.filter(m => m.role === 'leader').length,
        } as TeamWithMembers;
      });
    },
    enabled: !!orgId,
  });
}

// Fetch team members for a team
export function useTeamMembers(teamId: string | undefined) {
  return useQuery({
    queryKey: ["crm-team-members", teamId],
    queryFn: async () => {
      if (!teamId) return [];
      
      const { data: members, error: membersError } = await supabase
        .from("crm_team_members")
        .select("*")
        .eq("team_id", teamId);
      
      if (membersError) throw membersError;
      
      const userIds = members.map(m => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", userIds);
      
      if (profilesError) throw profilesError;
      
      return members.map(m => ({
        ...m,
        role: m.role as TeamRole,
        profile: profiles.find(p => p.user_id === m.user_id) || null,
      })) as TeamMemberWithProfile[];
    },
    enabled: !!teamId,
  });
}

// Create team
export function useCreateTeam() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (team: { name: string; description?: string; organization_id?: string }) => {
      const { data, error } = await supabase
        .from("crm_teams")
        .insert(team as any)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-teams"] });
      queryClient.invalidateQueries({ queryKey: ["crm-teams-with-counts"] });
      toast.success("Equipe criada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar equipe: ${error.message}`);
    },
  });
}

// Update team
export function useUpdateTeam() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Team> & { id: string }) => {
      const { data, error } = await supabase
        .from("crm_teams")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-teams"] });
      queryClient.invalidateQueries({ queryKey: ["crm-teams-with-counts"] });
      queryClient.invalidateQueries({ queryKey: ["crm-team"] });
      toast.success("Equipe atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar equipe: ${error.message}`);
    },
  });
}

// Delete team
export function useDeleteTeam() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("crm_teams")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-teams"] });
      queryClient.invalidateQueries({ queryKey: ["crm-teams-with-counts"] });
      toast.success("Equipe removida com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover equipe: ${error.message}`);
    },
  });
}

// Add team member
export function useAddTeamMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ team_id, user_id, role, organization_id }: { team_id: string; user_id: string; role: TeamRole; organization_id?: string }) => {
      const { data, error } = await supabase
        .from("crm_team_members")
        .insert({ team_id, user_id, role, organization_id } as any)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-teams"] });
      queryClient.invalidateQueries({ queryKey: ["crm-teams-with-counts"] });
      queryClient.invalidateQueries({ queryKey: ["crm-team"] });
      queryClient.invalidateQueries({ queryKey: ["crm-team-members"] });
      toast.success("Membro adicionado à equipe!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao adicionar membro: ${error.message}`);
    },
  });
}

// Update team member role
export function useUpdateTeamMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: TeamRole }) => {
      const { data, error } = await supabase
        .from("crm_team_members")
        .update({ role })
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-teams"] });
      queryClient.invalidateQueries({ queryKey: ["crm-teams-with-counts"] });
      queryClient.invalidateQueries({ queryKey: ["crm-team"] });
      queryClient.invalidateQueries({ queryKey: ["crm-team-members"] });
      toast.success("Papel do membro atualizado!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar papel: ${error.message}`);
    },
  });
}

// Remove team member
export function useRemoveTeamMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("crm_team_members")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-teams"] });
      queryClient.invalidateQueries({ queryKey: ["crm-teams-with-counts"] });
      queryClient.invalidateQueries({ queryKey: ["crm-team"] });
      queryClient.invalidateQueries({ queryKey: ["crm-team-members"] });
      toast.success("Membro removido da equipe!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover membro: ${error.message}`);
    },
  });
}

// Get least-loaded member from a team (weighted by active opportunities + pending tasks)
export async function getLeastLoadedTeamMember(teamId: string, organizationId?: string): Promise<string | null> {
  try {
    const { data: members, error } = await supabase
      .from("crm_team_members")
      .select("user_id")
      .eq("team_id", teamId);

    if (error || !members || members.length === 0) return null;
    if (members.length === 1) return members[0].user_id;

    const userIds = members.map(m => m.user_id);

    // Count active opportunities per member (not won/lost/discarded)
    let oppsQuery = supabase
      .from("crm_opportunities")
      .select("assigned_to")
      .in("assigned_to", userIds)
      .is("closed_at", null)
      .is("won_at", null)
      .is("lost_at", null);
    if (organizationId) oppsQuery = oppsQuery.eq("organization_id", organizationId);
    const { data: opps } = await oppsQuery;

    // Count pending tasks per member (not completed)
    let tasksQuery = supabase
      .from("crm_tasks")
      .select("assigned_to")
      .in("assigned_to", userIds)
      .is("completed_at", null);
    if (organizationId) tasksQuery = tasksQuery.eq("organization_id", organizationId);
    const { data: tasks } = await tasksQuery;

    // Calculate workload score: (active_opps * 2) + pending_tasks
    const scores = userIds.map(uid => {
      const oppCount = opps?.filter(o => o.assigned_to === uid).length || 0;
      const taskCount = tasks?.filter(t => t.assigned_to === uid).length || 0;
      return { userId: uid, score: (oppCount * 2) + taskCount };
    });

    const minScore = Math.min(...scores.map(s => s.score));
    const candidates = scores.filter(s => s.score === minScore);

    // Random pick among tied candidates
    return candidates[Math.floor(Math.random() * candidates.length)].userId;
  } catch (err) {
    console.error("getLeastLoadedTeamMember failed, falling back to random:", err);
    // Fallback to pure random
    const { data: members } = await supabase
      .from("crm_team_members")
      .select("user_id")
      .eq("team_id", teamId);
    if (!members || members.length === 0) return null;
    return members[Math.floor(Math.random() * members.length)].user_id;
  }
}

// Legacy alias kept for backwards compatibility
export const getRandomTeamMember = getLeastLoadedTeamMember;
